import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Col, DatePicker, Form, Input, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateAgentLicense, useUpdateAgentLicense } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { FinancialSector } from "../../enums";
import { AgentLicense, CreateUpdateAgentLicense } from "../../types";

interface Props {
  open: boolean;
  agentId: UUID;
  defaultLicenseValue?: string;
  license?: AgentLicense;
  onFormCancel: () => void;
}

const AgentLicenseForm = ({ open, agentId, defaultLicenseValue, license, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateAgentLicense>();
  const createAgentLicense = useCreateAgentLicense();
  const updateAgentLicense = useUpdateAgentLicense();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (license) {
      form.setFieldsValue({ ...license });
    } else {
      form.setFieldsValue({ value: defaultLicenseValue });
    }
  }, [open, defaultLicenseValue, license, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (license) {
          updateAgentLicense.mutate(
            { id1: agentId, id2: license.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "agent.license.attrs", error.violations),
            },
          );
        } else {
          createAgentLicense.mutate(
            { id: agentId, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "agent.license.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t(license ? "agent.license.titles.update" : "agent.license.titles.create")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createAgentLicense.isPending || updateAgentLicense.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={license} />

      <Form form={form} layout="vertical" name="agentLicenseForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item name="sector" label={t("agent.enums.financialSector._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                options={Object.keys(FinancialSector).map((sector) => ({
                  value: sector,
                  label: t("agent.enums.financialSector." + sector),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="value" label={t("agent.license.attrs.value")} rules={[validations.size(1, 64)]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="registrationProposalDate"
              label={t("agent.license.attrs.registrationProposalDate")}
              rules={[validations.notNull]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} showNow={false} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.registrationProposalDate !== next.registrationProposalDate}
            >
              {({ getFieldValue }) => {
                const registrationProposalDate = getFieldValue("registrationProposalDate") as string | undefined;
                return (
                  <Form.Item
                    name="registrationDate"
                    label={t("agent.license.attrs.registrationDate")}
                    rules={[
                      registrationProposalDate
                        ? validations.notBefore(
                            registrationProposalDate,
                            t("agent.license.attrs.registrationProposalDate"),
                          )
                        : validations.none,
                    ]}
                    {...datePickerFormItemProps}
                  >
                    <DatePicker
                      {...datePickerClearableProps}
                      showNow={false}
                      minDate={toDate(registrationProposalDate)}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.registrationProposalDate !== next.registrationProposalDate ||
              prev.registrationDate !== next.registrationDate ||
              prev.deletionProposalDate !== next.deletionProposalDate
            }
          >
            {({ getFieldValue }) => {
              const registrationProposalDate = getFieldValue("registrationProposalDate") as string | undefined;
              const registrationDate = getFieldValue("registrationDate") as string | undefined;
              const deletionProposalDate = getFieldValue("deletionProposalDate") as string | undefined;

              return (
                <>
                  <Col span={12}>
                    <Form.Item
                      name="deletionProposalDate"
                      label={t("agent.license.attrs.deletionProposalDate")}
                      rules={[
                        registrationDate
                          ? validations.notSameOrBefore(registrationDate, t("agent.license.attrs.registrationDate"))
                          : registrationProposalDate
                            ? validations.notSameOrBefore(
                                registrationProposalDate,
                                t("agent.license.attrs.registrationProposalDate"),
                              )
                            : validations.none,
                      ]}
                      {...datePickerFormItemProps}
                    >
                      <DatePicker
                        {...datePickerClearableProps}
                        showNow={false}
                        minDate={(toDate(registrationDate) ?? toDate(registrationProposalDate))?.add(1, "day")}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="deletionDate"
                      label={t("agent.license.attrs.deletionDate")}
                      rules={[
                        deletionProposalDate
                          ? validations.notBefore(deletionProposalDate, t("agent.license.attrs.deletionProposalDate"))
                          : registrationDate
                            ? validations.notSameOrBefore(registrationDate, t("agent.license.attrs.registrationDate"))
                            : registrationProposalDate
                              ? validations.notSameOrBefore(
                                  registrationProposalDate,
                                  t("agent.license.attrs.registrationProposalDate"),
                                )
                              : validations.none,
                      ]}
                      {...datePickerFormItemProps}
                    >
                      <DatePicker
                        {...datePickerClearableProps}
                        showNow={false}
                        minDate={
                          toDate(deletionProposalDate) ??
                          (toDate(registrationDate) ?? toDate(registrationProposalDate))?.add(1, "day")
                        }
                      />
                    </Form.Item>
                  </Col>
                </>
              );
            }}
          </Form.Item>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="note" label={t("agent.license.attrs.note")} rules={[validations.size(1, 8192)]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AgentLicenseForm;
